import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, Send, CheckCircle } from 'lucide-react';
import { Button } from './components/ui/button';

const AvisPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: searchParams.get('nom') || '',
    rating: 5,
    comment: '',
    event_type: searchParams.get('event') || ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/reviews/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de votre avis');
      }

      setIsSuccess(true);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Merci pour votre avis !</h2>
          <p className="text-gray-600 mb-6">
            Votre témoignage a été reçu et sera publié après vérification. Nous apprécions vraiment votre retour ! 🎉
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => window.location.href = '/'}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-3 rounded-lg font-semibold"
            >
              Retour à l'accueil
            </Button>
            <p className="text-sm text-gray-500">
              Vous pouvez fermer cette page
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-8 rounded-t-2xl text-center">
          <h1 className="text-4xl font-bold mb-2">GOS MÉDIA</h1>
          <h2 className="text-2xl font-semibold mb-2">Laissez-nous votre avis</h2>
          <p className="text-amber-100">Votre satisfaction est notre priorité</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Votre nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              minLength={2}
              maxLength={100}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-lg"
              placeholder="Jean Kabongo"
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Votre note <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-125"
                >
                  <Star
                    className={`h-12 w-12 ${
                      star <= (hoveredRating || formData.rating)
                        ? 'fill-amber-500 text-amber-500'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-lg font-semibold text-amber-600 mt-3">
              {formData.rating === 5 && '⭐ Excellent !'}
              {formData.rating === 4 && '⭐ Très bien'}
              {formData.rating === 3 && '⭐ Bien'}
              {formData.rating === 2 && '⭐ Moyen'}
              {formData.rating === 1 && '⭐ Décevant'}
            </p>
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type d'événement
            </label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-lg"
            >
              <option value="">Sélectionnez un type</option>
              <option value="Mariage">Mariage</option>
              <option value="Dot">Dot</option>
              <option value="Conférence">Conférence</option>
              <option value="Festival">Festival</option>
              <option value="Concert">Concert</option>
              <option value="Événement Gouvernemental">Événement Gouvernemental</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Votre commentaire <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-lg"
              placeholder="Partagez votre expérience avec GOS MÉDIA..."
            />
            <p className="text-sm text-gray-500 mt-1">
              {formData.comment.length}/500 caractères
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-4 rounded-lg font-semibold text-xl shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                Envoi en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="h-6 w-6" />
                Envoyer mon avis
              </span>
            )}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            Votre avis sera examiné avant publication
          </p>
        </form>
      </div>
    </div>
  );
};

export default AvisPage;
